import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isPastEvent, toSnapshot } from '../utils/favouriteSnapshot';

const STORAGE_KEY = 'eventhub.favourites';

const FavouritesContext = createContext({
  favourites: [],
  count: 0,
  isFavourite: () => false,
  toggle: () => {},
  remove: () => {},
  clearPast: () => {}
});

function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((entry) => entry?.id) : [];
  } catch {
    // Corrupt or blocked storage - start from empty rather than crashing.
    return [];
  }
}

function write(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Quota or private mode: the in-memory list still works for this session.
  }
}

export function FavouritesProvider({ children }) {
  const [favourites, setFavourites] = useState(read);

  useEffect(() => {
    write(favourites);
  }, [favourites]);

  // Keep two tabs of the app in step.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY) {
        setFavourites(read());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const ids = useMemo(() => new Set(favourites.map((entry) => entry.id)), [favourites]);

  const toggle = useCallback((event) => {
    const id = String(event._id);
    setFavourites((current) =>
      current.some((entry) => entry.id === id)
        ? current.filter((entry) => entry.id !== id)
        : [{ id, savedAt: Date.now(), event: toSnapshot(event) }, ...current]
    );
  }, []);

  const remove = useCallback((id) => {
    setFavourites((current) => current.filter((entry) => entry.id !== String(id)));
  }, []);

  const clearPast = useCallback(() => {
    setFavourites((current) => current.filter((entry) => !isPastEvent(entry.event)));
  }, []);

  const value = useMemo(() => ({
    favourites,
    count: favourites.length,
    isFavourite: (id) => ids.has(String(id)),
    toggle,
    remove,
    clearPast
  }), [favourites, ids, toggle, remove, clearPast]);

  return <FavouritesContext.Provider value={value}>{children}</FavouritesContext.Provider>;
}

export function useFavourites() {
  return useContext(FavouritesContext);
}
